/**
 * Hooks de TanStack Query para el módulo de Cuentas BO.
 *
 * @taskId T473
 * @see functional/Specs/specs_cycle_4.md HU-27-FE
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  exportAccountsCsv,
  getAccountDetail,
  getAccounts,
  getTransactionDetail,
  getTransactionHistory,
  updateAccountStatus,
} from '../../../api/accounts-bo.api';
import type {
  Account,
  AccountDetail,
  AccountListParams,
  AccountListResponse,
  TransactionDetail,
  TransactionHistoryParams,
  TransactionHistoryResponse,
  UpdateAccountStatusPayload,
} from '../types/account.types';

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const accountKeys = {
  all: ['accounts-bo'] as const,
  lists: () => [...accountKeys.all, 'list'] as const,
  list: (params: AccountListParams) => [...accountKeys.lists(), params] as const,
  details: () => [...accountKeys.all, 'detail'] as const,
  detail: (id: string) => [...accountKeys.details(), id] as const,
  transactions: (accountId: string) => [...accountKeys.all, 'transactions', accountId] as const,
  transactionList: (params: TransactionHistoryParams) =>
    [...accountKeys.transactions(params.accountId), params] as const,
  transactionDetail: (accountId: string, transactionId: string) =>
    [...accountKeys.transactions(accountId), 'detail', transactionId] as const,
};

// ─── Query Hooks ────────────────────────────────────────────────────────────

/**
 * Hook para listar cuentas con paginación y filtros.
 */
export function useAccounts(params: AccountListParams) {
  return useQuery<AccountListResponse, Error>({
    queryKey: accountKeys.list(params),
    queryFn: () => getAccounts(params),
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook para obtener detalle de una cuenta.
 */
export function useAccountDetail(id: string | undefined) {
  return useQuery<AccountDetail, Error>({
    queryKey: accountKeys.detail(id ?? ''),
    queryFn: () => getAccountDetail(id!),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

/**
 * Hook para obtener historial de transacciones de una cuenta.
 */
export function useTransactionHistory(params: TransactionHistoryParams | null) {
  return useQuery<TransactionHistoryResponse, Error>({
    queryKey: params ? accountKeys.transactionList(params) : ['disabled'],
    queryFn: () => getTransactionHistory(params!),
    enabled: Boolean(params?.accountId),
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook para obtener detalle de una transacción.
 */
export function useTransactionDetail(
  accountId: string | undefined,
  transactionId: string | undefined,
) {
  return useQuery<TransactionDetail, Error>({
    queryKey: accountKeys.transactionDetail(accountId ?? '', transactionId ?? ''),
    queryFn: () => getTransactionDetail(accountId!, transactionId!),
    enabled: Boolean(accountId) && Boolean(transactionId),
    staleTime: 60_000,
  });
}

// ─── Mutation Hooks ─────────────────────────────────────────────────────────

interface UpdateAccountStatusVariables {
  id: string;
  payload: UpdateAccountStatusPayload;
}

/**
 * Hook para cambiar estado de una cuenta.
 * Solo PSP_ADMIN (RULE-29).
 * Motivo obligatorio (RULE-16).
 */
export function useUpdateAccountStatus() {
  const queryClient = useQueryClient();

  return useMutation<Account, Error, UpdateAccountStatusVariables>({
    mutationFn: ({ id, payload }) => updateAccountStatus(id, payload),
    onSuccess: (_data, variables) => {
      // Actualizar cache del detalle
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(variables.id) });
      // Invalidar listados
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
    },
  });
}

interface ExportAccountsCsvVariables {
  estado?: string;
  merchantId?: string;
  search?: string;
}

/**
 * Hook para exportar cuentas a CSV.
 */
export function useExportAccountsCsv() {
  return useMutation<Blob, Error, ExportAccountsCsvVariables>({
    mutationFn: (params) => exportAccountsCsv(params),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cuentas-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });
}
